/*
 *  ************************************************************************
 *  *  © [2015 - 2024] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from 'react';
import get from 'lodash/get';
import { SvgHandler } from '../../../svg-handler/svg-handler';
import success from '../../../../../assets/icons/success.svg';
import { getTextContrast, getSubscriptionRedirect, getPath } from '../../../../utils';
import { checkIfSingleSignOn, navigateToAuthDomain } from '../../login/sso-utils';
import { QUILL_EMPTY_STRING } from '../../../../constants';
import './success.m.css';
import { useSelector } from 'react-redux';

export const SubscriptionSuccess = props => {
  const sketchesHost = get(props, ['config', 'sketches-host'], '');
  const pbConfig = get(props, ['config', 'pagebuilder-config'], {});
  const { primaryColor = '#4860BC', darkPrimaryColor = '#4860BC' } = get(pbConfig, ['general'], {});
  const sso = get(pbConfig, ['general', 'sso'], {});
  const enableDarkMode = useSelector(state => get(state, ['header', 'isDarkModeEnabled'], false));
  const thankYouPageSettings = get(pbConfig, ['subscription', 'pageSetting'], {});
  const enableLoginlessSubscription = get(thankYouPageSettings, ['enableLoginlessSubscription'], false);
  const member = useSelector(state => get(state, ['member'], null)) || props.member;
  const isMemberLoggedIn = Boolean(member && (member.id || member.email));

  const isLoginless = enableLoginlessSubscription && !isMemberLoggedIn;

  const {
    continueReading,
    continueToSignIn,
    loginlessContinueButtonText,
    goToHome,
    goToProfile,
    buttonColor = primaryColor,
    darkButtonColor = darkPrimaryColor
  } = thankYouPageSettings;

  const { thankYouPageTitle, thankYouPageDescription } = getTitleAndDescription(
    thankYouPageSettings,
    enableLoginlessSubscription
  );

  const buttonBgColor = enableDarkMode ? darkButtonColor : buttonColor;
  const textColor = getTextContrast(buttonBgColor) === 'dark' ? '#000' : '#fff';
  const whiteListedUrlParams = get(pbConfig, ['general', 'others', 'whiteListedUrlParams'], []);

  const continueButtonText = isLoginless
    ? (loginlessContinueButtonText ||
       continueToSignIn ||
       (continueReading && continueReading !== 'Continue Reading' ? continueReading : 'Continue to Sign In / Sign Up'))
    : (continueReading || 'Continue Reading');

  return (
    <div styleName='wrapper' data-test-id='thank-you-page'>
      <span data-test-id='subscription-success-icon'>
        <SvgHandler xlinkHref={success} width='24' height='24' viewBox='0 0 24 24' clazzName={''} />
      </span>
      <div styleName='title' dangerouslySetInnerHTML={{ __html: thankYouPageTitle }} data-test-id='subscription-success-heading' />
      <div styleName='content' dangerouslySetInnerHTML={{ __html: thankYouPageDescription }} data-test-id='subscription-success-message' />
      <div styleName='button-wrapper'>
        <div>
          <ContinueReadingButton
            continueReading={continueButtonText}
            textColor={textColor}
            buttonBgColor={buttonBgColor}
            isLoginless={isLoginless}
            sketchesHost={sketchesHost}
            whiteListedUrlParams={whiteListedUrlParams}
            sso={sso}
          />
          {!isLoginless && (
            <GoToProfileButton
              goToProfile={goToProfile}
              sketchesHost={sketchesHost}
              whiteListedUrlParams={whiteListedUrlParams}
            />
          )}
        </div>
      </div>
      {!isLoginless && <GoToHomeLink sketchesHost={sketchesHost} goToHome={goToHome} />}
    </div>
  );
};

function getTitleAndDescription (thankYouPageSettings = {}, enableLoginlessSubscription = false) {
  let {
    thankYouPageTitle,
    thankYouPageDescription,
    loginlessThankYouPageTitle,
    loginlessThankYouPageDescription
  } = thankYouPageSettings;

  const isDefaultTitle =
    !thankYouPageTitle ||
    thankYouPageTitle === QUILL_EMPTY_STRING ||
    (typeof thankYouPageTitle === 'string' &&
      thankYouPageTitle.replace(/<[^>]*>/g, '').trim() === 'Thank you for placing your order!');

  const isDefaultDesc =
    !thankYouPageDescription ||
    thankYouPageDescription === QUILL_EMPTY_STRING ||
    (typeof thankYouPageDescription === 'string' &&
      thankYouPageDescription.replace(/<[^>]*>/g, '').trim() ===
        'We kindly ask for your patience as we process it (approx. 15min). Please check your email for further details and updates');

  // loginless title/description takes precedence whenever the feature is enabled, regardless of login state
  if (enableLoginlessSubscription) {
    const title =
      loginlessThankYouPageTitle && loginlessThankYouPageTitle !== QUILL_EMPTY_STRING
        ? loginlessThankYouPageTitle
        : !isDefaultTitle
          ? thankYouPageTitle
          : 'Subscription Successful';

    const description =
      loginlessThankYouPageDescription && loginlessThankYouPageDescription !== QUILL_EMPTY_STRING
        ? loginlessThankYouPageDescription
        : !isDefaultDesc
          ? thankYouPageDescription
          : 'Your subscription is active. Just log in or create your account to start enjoying your premium content.';

    return {
      thankYouPageTitle: title,
      thankYouPageDescription: description
    };
  }

  if (!thankYouPageTitle || thankYouPageTitle === QUILL_EMPTY_STRING) {
    thankYouPageTitle = 'Thank you for placing your order!';
  }

  if (!thankYouPageDescription || thankYouPageDescription === QUILL_EMPTY_STRING) {
    thankYouPageDescription =
      'We kindly ask for your patience as we process it (approx. 15min). Please check your email for further details and updates';
  }

  return { thankYouPageTitle, thankYouPageDescription };
}

function GoToHomeLink ({ goToHome, sketchesHost }) {
  const href = getPath({ sketchesHost, relativePath: `/` });
  return (
    <a href={href} styleName='goto-home-link ' data-test-id='subscription-go-home-link'>
      {goToHome || 'Go to Home'}
    </a>
  );
}

function GoToProfileButton ({ goToProfile, sketchesHost, whiteListedUrlParams = [] }) {
  const fallbackText = 'Manage Subscriptions';
  const href = getPath({
    sketchesHost,
    relativePath: '/manage-subscriptions',
    injectCustomParams: true,
    whiteListedUrlParams,
  });
  return (
    <a href={href} styleName='button goto-profile-button' data-test-id='subscription-manage-btn'>
      {goToProfile || fallbackText}
    </a>
  );
}

function ContinueReadingButton ({
  continueReading,
  textColor,
  buttonBgColor,
  isLoginless = false,
  sketchesHost = '',
  whiteListedUrlParams = [],
  sso = {}
}) {
  const handleClick = () => {    
    const redirectUrl = getSubscriptionRedirect() || window?.qtMountAt || '/';
    
    if (isLoginless) {
      if (checkIfSingleSignOn(sso)) {

        navigateToAuthDomain(sso?.bridgeKeeperClientId, sketchesHost, whiteListedUrlParams, isLoginless, redirectUrl);
        return;
      }

      const loginUrl = getPath({
        sketchesHost,
        relativePath: '/sign-in',
        injectCustomParams: true,
        whiteListedUrlParams
      });
      window.location.href = loginUrl;
      return;
    }

    window.location.href = redirectUrl;
  };

  return (
    <div
      onClick={handleClick}
      styleName='button continue-reading-button'
      style={{ color: textColor, ...(buttonBgColor ? { backgroundColor: buttonBgColor } : {}) }}
      data-test-id={isLoginless ? 'subscription-signin-signup-btn' : 'subscription-continue-btn'}
    >
      {continueReading || 'Continue Reading'}
    </div>
  );
}
